/** Change a training course's date. If trainees are booked, staff choose: move them with the course, or transfer them to another course. */
import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';

const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function ChangeCourseDateDialog({ course, traineeCount, open, onClose, onChanged }) {
  const [date, setDate] = useState('');
  const [mode, setMode] = useState('move_with_course');
  const [target, setTarget] = useState('');
  const [courses, setCourses] = useState([]);
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDate(''); setMode('move_with_course'); setTarget(''); } }, [open, course?.id]);
  useEffect(() => {
    if (!open || !traineeCount) return;
    fetch(`${API_URL}/api/staff/training-courses`, { credentials: 'include' }).then((r) => r.json()).then((rows) => setCourses((rows || []).filter((c) => c.id !== course?.id))).catch(() => {});
  }, [open, traineeCount, course?.id]);
  const targets = useMemo(() => courses.filter((c) => !c.date || c.date >= new Date().toISOString().slice(0, 10)).sort((a, b) => (a.date || '').localeCompare(b.date || '')), [courses]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/training-courses/${course.id}/change-date`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_date: date, trainees: mode, target_course_id: mode === 'transfer' ? target : null, notify_email: email, notify_sms: sms }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not change the date');
      const n = d.notified;
      toast.success(`Date changed to ${fmt(d.course.date)}`, { description: traineeCount ? (mode === 'transfer' ? `${d.moved.length} trainee${d.moved.length === 1 ? '' : 's'} transferred${d.skipped_email_collision?.length ? `, ${d.skipped_email_collision.length} skipped (already on that course)` : ''}` : `${traineeCount} trainee${traineeCount === 1 ? '' : 's'} moved with the course`) + (n ? ` · ${n.email} emailed, ${n.sms} texted` : '') : undefined });
      onChanged?.(d); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const ready = date && date !== course?.date && (mode !== 'transfer' || target);
  if (!course) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="change-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5" /> Change course date</DialogTitle>
          <DialogDescription>{course.name} · currently {fmt(course.date)}</DialogDescription>
        </DialogHeader>
        <label className="text-sm font-semibold text-gray-800">New date<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="change-date-input" /></label>
        {traineeCount > 0 && (
          <div className="space-y-2" data-testid="change-date-trainees">
            <p className="text-sm text-gray-700"><b>{traineeCount} trainee{traineeCount === 1 ? ' is' : 's are'}</b> booked on {fmt(course.date)}. What should happen to them?</p>
            <label className={`flex items-start gap-2 border-2 rounded-lg p-3 cursor-pointer ${mode === 'move_with_course' ? 'border-gray-900 bg-gray-50' : 'border-gray-200'}`} data-testid="change-date-mode-move">
              <input type="radio" className="mt-1" checked={mode === 'move_with_course'} onChange={() => setMode('move_with_course')} />
              <span className="text-sm"><b>Move them to the new date</b><br /><span className="text-gray-500">They stay on this course and are told about the new date.</span></span>
            </label>
            <label className={`flex items-start gap-2 border-2 rounded-lg p-3 cursor-pointer ${mode === 'transfer' ? 'border-gray-900 bg-gray-50' : 'border-gray-200'}`} data-testid="change-date-mode-transfer">
              <input type="radio" className="mt-1" checked={mode === 'transfer'} onChange={() => setMode('transfer')} />
              <span className="text-sm flex-1"><b>Move them to a different course</b><br /><span className="text-gray-500">Transfer everyone to another date first, then change this course's date.</span>
                {mode === 'transfer' && (
                  <select value={target} onChange={(e) => setTarget(e.target.value)} className="mt-2 w-full border rounded-md h-9 px-2 text-sm bg-white" data-testid="change-date-target">
                    <option value="">Choose a course…</option>
                    {targets.map((c) => <option key={c.id} value={c.id}>{fmt(c.date)} — {c.name} ({c.registrations_total || 0} booked)</option>)}
                  </select>
                )}
              </span>
            </label>
            <div className="flex gap-4 text-sm text-gray-700 pt-1">
              <label className="flex items-center gap-2"><Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} data-testid="change-date-notify-email" /> Email trainees</label>
              <label className="flex items-center gap-2"><Checkbox checked={sms} onCheckedChange={(v) => setSms(!!v)} data-testid="change-date-notify-sms" /> Text trainees</label>
            </div>
          </div>
        )}
        <Button onClick={submit} disabled={!ready || busy} className="w-full h-11 bg-gray-900 text-white font-bold" data-testid="change-date-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : date ? `Change date to ${fmt(date)}` : 'Pick a new date'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
